import React from 'react';
import {useTranslation} from 'react-i18next';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {PortalUsageEmptyState} from '../../portal_usage/PortalUsageEmptyState';
import {
    activeDaysPerWeek,
    buildTopicRows,
    clientQueryCount,
    clientStepCount,
    resolveTopic,
} from '../../portal_usage/portalUsageState';

/**
 * «Para qué lo usa»: la respuesta, antes que la evidencia.
 *
 * Va ARRIBA del feed de preguntas y no adentro, a propósito. Todo lo que muestra sale de nombres de
 * tool y del catálogo de campos de Dashbo — ni una palabra escrita por una persona—, así que el día
 * que se apague la política de texto se apaga UNA caja y esta sigue contestando la pregunta
 * comercial: si lo usa y para qué. Si viviera dentro del feed, apagar el texto la dejaría huérfana.
 *
 * Es una lista rankeada con barra, no chips: con chips el conteo compite con la etiqueta, y con
 * barra la frecuencia relativa se lee sin leer un solo número.
 */
export function PortalAssistantTopics({usage}) {
    const {t} = useTranslation();
    const assistant = (usage && usage.assistant) || {};
    const rows = buildTopicRows(assistant);
    const queries = clientQueryCount(usage);
    const steps = clientStepCount(usage);
    const fieldUsage = assistant.field_usage || {};
    const channels = fieldUsage.channels || [];
    const fields = fieldUsage.fields || [];

    // El asistente se conectó y trabajó, pero el cliente nunca pidió un número. Es accionable y
    // merece decirse, no un bloque vacío.
    const onlyContext = queries === 0 && typeof steps === 'number' && steps > 0;
    if (!rows.length && !onlyContext) return null;

    const perWeek = activeDaysPerWeek(
        (assistant.client && assistant.client.active_days) || 0,
        (usage && usage.window_days) || 0);

    return (
        <div className="box pu-box">
            <div className="pu-box__head">
                <h3 className="pu-box__title">{t('portal_usage.topics_title', 'Para qué lo usa')}</h3>
            </div>

            {onlyContext ? <PortalUsageEmptyState kind="CONTEXT_ONLY" count={steps}/> : (
                <>
                    {perWeek > 0 ? (
                        <p className="pu-topics__sub">
                            {perWeek === 1
                                ? t('portal_usage.topics_sub_one', 'Consulta 1 día por semana, en promedio.')
                                : t('portal_usage.topics_sub', 'Consulta {{n}} días por semana, en promedio.',
                                    {n: perWeek})}
                        </p>
                    ) : null}

                    <ul className="pu-topics">
                        {rows.map((row) => {
                            const label = resolveTopic(row.topic);
                            return (
                                <li className="pu-topic" key={row.topic}>
                                    <span className="pu-topic__lbl">{t(label.key, label.fallback)}</span>
                                    <span className="pu-topic__bar">
                                        <span style={{width: `${Math.round(row.ratio * 100)}%`}}/>
                                    </span>
                                    <span className="pu-topic__n">{row.count}</span>
                                </li>
                            );
                        })}
                    </ul>

                    {channels.length || fields.length ? (
                        <div className="pu-focus">
                            {channels.length ? (
                                <div className="pu-focus__group">
                                    <span className="pu-focus__lbl">
                                        {t('portal_usage.topics_focus_channels', 'Canales')}
                                    </span>
                                    {/* ⚠️ Los canales llegan como el MCP los loguea (`FACEBOOK`, no
                                        `META`): `ChannelLogo` no renderiza nada con `META` y no
                                        avisa. Sin espaciados propios, que engordan la fila. */}
                                    {channels.map(channel => (
                                        <ChannelLogo
                                            key={channel} channel={channel}
                                            hspace={0} vspace={0} marginLeft={0} padding={0}
                                        />
                                    ))}
                                </div>
                            ) : null}
                            {fields.length ? (
                                <div className="pu-focus__group">
                                    <span className="pu-focus__lbl">
                                        {t('portal_usage.topics_focus_metrics', 'Métricas')}
                                    </span>
                                    {fields.map(field => (
                                        <span className="pu-focus__chip" key={field.id}>
                                            {field.name
                                                || t('portal_usage.field_custom_metric', 'Métrica personalizada')}
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}

export default PortalAssistantTopics;
